import React from "react";
import { Sparkles, Wind, Droplets, Check } from "lucide-react";

const SERVICES = [
  {
    id: "exterior",
    label: "Exterior Detail",
    price: 50,
    icon: Droplets,
    image:
      "https://images.unsplash.com/photo-1761312834150-4beefff097a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjYXIlMjBkZXRhaWxpbmclMjBmb2FtJTIwd2FzaHxlbnwwfHx8fDE3NzgxNzc3NzR8MA&ixlib=rb-4.1.0&q=85",
    desc: "Hand foam wash, decontamination, tire shine, and a streak-free finish.",
    features: [
      "Two-bucket hand wash",
      "Wheels &amp; tires deep clean",
      "Glass &amp; trim dressing",
      "Spot decontamination",
    ],
  },
  {
    id: "interior",
    label: "Interior Detail",
    price: 65,
    icon: Wind,
    popular: false,
    image:
      "https://images.unsplash.com/photo-1661963603110-632028a09c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjBjYXIlMjBpbnRlcmlvciUyMGNsZWFufGVufDB8fHx8MTc3ODE3Nzc3NXww&ixlib=rb-4.1.0&q=85",
    desc: "Full vacuum, steam, shampoo, and conditioning — every nook included.",
    features: [
      "Deep vacuum &amp; air-blast",
      "Carpet &amp; seat shampoo",
      "Dash, trim, and console wipe",
      "Interior glass cleaning",
    ],
  },
  {
    id: "full",
    label: "Full Detail",
    price: 110,
    icon: Sparkles,
    popular: true,
    image:
      "https://images.pexels.com/photos/261985/pexels-photo-261985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    desc: "Everything inside &amp; out. The signature B&amp;B treatment.",
    features: [
      "Complete exterior detail",
      "Complete interior detail",
      "Hand wax &amp; sealant",
      "Tire shine &amp; trim restore",
    ],
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-3">
            What we do
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter">
            Pick your level of clean.
          </h2>
          <p className="mt-4 text-base md:text-lg text-zinc-400">
            Honest pricing. Premium materials. The kind of care that earns 5-star reviews.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((svc, idx) => {
            const Icon = svc.icon;
            return (
              <div
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                className={`group relative rounded-2xl overflow-hidden border bg-zinc-900/40 backdrop-blur-md transition-all duration-500 hover:-translate-y-1 ${
                  svc.popular
                    ? "border-cyan-500/40 shadow-[0_0_40px_rgba(34,211,238,0.15)]"
                    : "border-zinc-800 hover:border-cyan-500/40"
                }`}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                {svc.popular && (
                  <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-cyan-400 text-zinc-950 text-[10px] font-black uppercase tracking-wider">
                    Most Popular
                  </div>
                )}

                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.label}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>

                <div className="p-7">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-9 w-9 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold tracking-tight" data-testid={`service-name-${svc.id}`}>
                      {svc.label}
                    </h3>
                  </div>

                  <p className="text-sm text-zinc-400 mt-2" dangerouslySetInnerHTML={{ __html: svc.desc }} />

                  <div className="mt-6 flex items-baseline gap-1.5">
                    <span
                      className="text-5xl font-black tracking-tighter text-cyan-400"
                      data-testid={`service-price-${svc.id}`}
                    >
                      ${svc.price}
                    </span>
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">starting</span>
                  </div>

                  <ul className="mt-6 space-y-2 text-sm text-zinc-300">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: f }} />
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#booking"
                    onClick={() => {
                      try {
                        sessionStorage.setItem("preselect_service", svc.id);
                      } catch {}
                    }}
                    data-testid={`service-book-${svc.id}`}
                    className={`mt-7 block text-center font-bold py-3 rounded-full transition-all ${
                      svc.popular
                        ? "bg-cyan-400 text-zinc-950 hover:bg-cyan-300 shadow-[0_0_20px_rgba(34,211,238,0.3)]"
                        : "border border-zinc-700 text-zinc-100 hover:border-cyan-400 hover:text-cyan-400"
                    }`}
                  >
                    Book {svc.label}
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-zinc-500" data-testid="services-disclaimer">
          Prices may vary based on vehicle size, condition, and selected add-ons. Final quote confirmed at booking.
        </p>
      </div>
    </section>
  );
}
